import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import {
  addMonths,
  fromISO,
  monthCells,
  startOfMonth,
} from '@/features/institute/calendar/utils/dates';
import { cn } from '@/lib/utils';
import type { ParentWorkItem, ParentWorkState } from '../../types/parent';
import { workState } from '../../utils';

export interface TaskCalendarCardProps {
  /**
   * Every task for the child, *unfiltered*.
   *
   * The calendar is how a parent sees the shape of the month, so it has to keep
   * showing the days that the filters have taken out of the list — otherwise
   * choosing "Completed" empties the calendar and the month looks free.
   */
  items: ParentWorkItem[];
  today: string;
  /** The day currently filtered to, if any. */
  selectedDay: string | null;
  onPickDay: (dateIso: string) => void;
}

/** Worst first: what one day's dot says when several things fall on it. */
const DOT: Record<ParentWorkState, string> = {
  OVERDUE: 'bg-rose-500',
  PENDING: 'bg-amber-500',
  UPCOMING: 'bg-sky-500',
  COMPLETED: 'bg-emerald-500',
};

const SEVERITY: ParentWorkState[] = ['OVERDUE', 'PENDING', 'UPCOMING', 'COMPLETED'];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function TaskCalendarCard({
  items,
  today,
  selectedDay,
  onPickDay,
}: TaskCalendarCardProps) {
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(today));

  const anchor = fromISO(monthAnchor);
  const cells = monthCells(anchor.getFullYear(), anchor.getMonth());

  /** The worst thing due on each day, keyed by `YYYY-MM-DD`. */
  const worstByDay = useMemo(() => {
    const byDay = new Map<string, ParentWorkState>();

    for (const item of items) {
      const state = workState(item, today);
      const current = byDay.get(item.dueDate);

      if (!current || SEVERITY.indexOf(state) < SEVERITY.indexOf(current)) {
        byDay.set(item.dueDate, state);
      }
    }

    return byDay;
  }, [items, today]);

  const monthLabel = new Intl.DateTimeFormat('en-IN', {
    month: 'long',
    year: 'numeric',
  }).format(anchor);

  const monthPrefix = monthAnchor.slice(0, 7);

  return (
    <section
      className="rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-xs flex flex-col"
      aria-label="Deadline calendar"
    >
      <header className="flex items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800 px-4 py-3">
        <button
          type="button"
          onClick={() => setMonthAnchor(addMonths(monthAnchor, -1))}
          aria-label="Previous month"
          className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>

        <h2 className="text-sm sm:text-base font-bold text-slate-800 dark:text-white" aria-live="polite">
          {monthLabel}
        </h2>

        <button
          type="button"
          onClick={() => setMonthAnchor(addMonths(monthAnchor, 1))}
          aria-label="Next month"
          className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </header>

      <div className="p-4">
        <div className="grid grid-cols-7 gap-1 text-center">
          {WEEKDAYS.map((weekday) => (
            <span key={weekday} className="pb-1.5 text-xs font-bold uppercase text-slate-400 dark:text-slate-500">
              {weekday}
            </span>
          ))}

          {cells.map((dateIso) => {
            const isThisMonth = dateIso.startsWith(monthPrefix);
            const isToday = dateIso === today;
            const isSelected = dateIso === selectedDay;
            const worst = worstByDay.get(dateIso);
            const dayNumber = Number(dateIso.slice(-2));

            const shell = cn(
              'relative grid h-8 w-8 sm:h-9 sm:w-9 mx-auto place-items-center rounded-xl text-xs sm:text-sm font-semibold tabular-nums transition-colors',
              isThisMonth ? 'text-slate-800 dark:text-slate-200' : 'text-slate-300 dark:text-slate-600',
              isToday && 'bg-indigo-600 font-black text-white shadow-xs',
              isSelected && !isToday && 'border-2 border-indigo-500 bg-indigo-50 dark:bg-indigo-950/50 font-bold text-indigo-600 dark:text-indigo-400',
            );

            const dot = worst && (
              <span
                aria-hidden
                className={cn(
                  'absolute bottom-1 h-1.5 w-1.5 rounded-full',
                  isToday ? 'bg-white' : DOT[worst],
                )}
              />
            );

            if (!worst) {
              return (
                <span key={dateIso} className={shell}>
                  {dayNumber}
                </span>
              );
            }

            return (
              <button
                key={dateIso}
                type="button"
                onClick={() => onPickDay(dateIso)}
                aria-pressed={isSelected}
                aria-label={`Show what is due on ${dateIso}`}
                className={cn(
                  shell,
                  'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                  !isToday && !isSelected && 'hover:bg-slate-100 dark:hover:bg-slate-800',
                )}
              >
                {dayNumber}
                {dot}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <ul className="mt-4 flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 border-t border-slate-100 dark:border-slate-800 pt-3 text-xs font-semibold text-slate-600 dark:text-slate-400">
          {(['OVERDUE', 'PENDING', 'UPCOMING', 'COMPLETED'] as ParentWorkState[]).map((state) => (
            <li key={state} className="inline-flex items-center gap-1.5">
              <span className={cn('h-2 w-2 rounded-full', DOT[state])} aria-hidden />
              <span>{state.charAt(0) + state.slice(1).toLowerCase()}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
